import isEmpty from "lodash/isEmpty";
import { fetchJSON } from "lib/fetch";
import { PriceEntity } from "../entity/price-entity/promotions.types";
import PromotionUtility from "./promotion-utility";
import { PromotionOptions } from "../entity/promotions-dtos.types";
import { THRESHOLD_FOLDER, THRESHOLD_SUFFIX, UI_FOLDER } from "../utils/consts";
import noop from "lodash/noop";

type CheckResult = {
    is_exist: boolean;
};

export type FetchEntityResult = {
    entity: PriceEntity;
};

export type FetchEntitiesResult = {
    price_entities: PriceEntity[];
};

type FailedUpdates = {
    price_entity_id: string;
    failure_type: string;
    failure_message: string;
};

export type UpdateEntitiesResult = {
    price_entities: PriceEntity[];
    failed_updates: FailedUpdates[];
};

export interface IPromotionsService {
    isPromotionNameExist(promotionName: string, merchantId: string): Promise<boolean>;

    deletePriceEntity(
        entityId: string,
        merchantId: string,
        folder?: string,
        withRelatedThreshold?: boolean,
    ): Promise<void>;

    fetchRelatedThresholdPriceEntity(entityId: string, merchantId: string): Promise<PriceEntity>;
}

class PromotionService extends PromotionUtility implements IPromotionsService {
    isPromotionNameExist(promotionName: string, merchantId: string): Promise<boolean> {
        if (!promotionName || promotionName.length === 0) {
            return Promise.resolve(false);
        }

        return fetchJSON<CheckResult>(
            `/api/v1/merchant/${merchantId}/pricing/entities/check/${encodeURIComponent(promotionName)}`,
            {
                method: "GET",
                noDomain: true,
            },
        ).then((result) => result.is_exist);
    }

    async deleteThresholdEntity(entityId: string, merchantId: string): Promise<void> {
        let entity;
        try {
            entity = await this.fetchRelatedThresholdPriceEntity(entityId, merchantId);
        } catch (e) {
            entity = null;
        }
        if (isEmpty(entity)) {
            return;
        }
        const options = {
            method: "DELETE",
            noDomain: true,
        };
        return fetchJSON(
            `/api/v1/merchant/${merchantId}/pricing/entities/${entityId}${THRESHOLD_SUFFIX}?folder=${THRESHOLD_FOLDER}`,
            options,
        );
    }

    deletePriceEntity(
        entityId: string,
        merchantId: string,
        folder = UI_FOLDER,
        withRelatedThreshold = true,
    ): Promise<void> {
        const options = {
            method: "DELETE",
            noDomain: true,
        };
        const successCallback =
            withRelatedThreshold && !entityId.includes(THRESHOLD_SUFFIX)
                ? () =>
                      fetchJSON(
                          `/api/v1/merchant/${merchantId}/pricing/entities/${entityId}${THRESHOLD_SUFFIX}?folder=${THRESHOLD_FOLDER}`,
                          options,
                      )
                : noop;

        return fetchJSON(`/api/v1/merchant/${merchantId}/pricing/entities/${entityId}?folder=${folder}`, options).then(
            () => {
                successCallback();
                return Promise.resolve();
            },
        );
    }

    bulkDeleteEntities(entityIds: string[], merchantId: string, folder = UI_FOLDER): Promise<void> {
        const data = {
            merchantId,
            folder,
            entityIds,
        };
        const options = {
            method: "POST",
            noDomain: true,
            body: JSON.stringify(data),
        };
        return fetchJSON(`/api/v1/merchant/${merchantId}/pricing/entities/bulk_delete`, options);
    }

    async bulkUpdateEntities(
        uiEntities: PriceEntity[],
        merchantId: string,
        enabled: boolean,
    ): Promise<UpdateEntitiesResult> {
        let entities = uiEntities;
        const thresholdPageSize = uiEntities.length > 10 ? uiEntities.length.toString() : "10";

        await this.fetchAllThresholdPriceEntity(merchantId, thresholdPageSize).then((thresholdEntities) => {
            if (thresholdEntities.length > 0) {
                const ids = uiEntities.map((entity) => entity.id);
                const filtered = thresholdEntities.filter((entity) =>
                    ids.includes(entity.id.replace("-threshold", "")),
                );
                filtered.forEach((entity) => (entity.enabled = enabled));

                entities = entities.concat(filtered);
            }
        });

        const data = {
            merchant_id: merchantId,
            price_entities: entities,
            include_disabled: true,
        };

        const options = {
            method: "POST",
            noDomain: true,
            body: JSON.stringify(data),
        };
        return fetchJSON(`/api/v1/merchant/${merchantId}/pricing/entities/bulk_update`, options);
    }

    fetchRelatedThresholdPriceEntity(entityId: string, merchantId: string): Promise<PriceEntity> {
        return fetchJSON<FetchEntityResult>(
            `/api/v1/merchant/${merchantId}/pricing/entities/${entityId}${THRESHOLD_SUFFIX}?folder=${THRESHOLD_FOLDER}&includeDisabled=true`,
            {
                method: "GET",
                noDomain: true,
            },
        ).then((result) => result.entity);
    }

    fetchAllThresholdPriceEntity(merchantId: string, pageSize: string): Promise<PriceEntity[]> {
        return fetchJSON<FetchEntitiesResult>(
            `/api/v1/merchant/${merchantId}/pricing/entities?folder=${THRESHOLD_FOLDER}&pageSize=${pageSize}&includeDisabled=true`,
            {
                method: "GET",
                noDomain: true,
            },
        ).then((result) => result.price_entities);
    }

    async createPromotion(
        options: PromotionOptions & { isThreshold: boolean; isQty?: boolean },
        promotionRequest: Partial<PriceEntity>,
    ): Promise<void> {
        const {
            selectedDiscounts,
            isEditMode,
            entityId,
            id,
            isThreshold,
            amountOf,
            isLineItemLevel,
            isQty,
            netTermsExtension,
        } = options;
        const additionalOptions = {
            priceActionId: entityId ?? id,
            isThreshold: isThreshold,
            amount: amountOf,
            isLineItemLevel: isLineItemLevel,
            isQty,
            netTermsExtension,
        };

        promotionRequest = this.addPricingActions(promotionRequest, additionalOptions, selectedDiscounts);
        const includeConditionalRule = !!(
            promotionRequest.price_condition?.conditional_date_range_condition?.date_from &&
            promotionRequest.price_condition?.conditional_date_range_condition?.date_to
        );
        if (isEditMode && !isThreshold) {
            await this.callUpdatePromotionPoint(
                options.merchant_id,
                JSON.stringify(promotionRequest),
                entityId,
                includeConditionalRule,
            );
        } else {
            await this.callCreatePromotionPoint(
                options.merchant_id,
                JSON.stringify(promotionRequest),
                includeConditionalRule,
            );
        }
    }
}

export const promotionsService = new PromotionService();
